import React from 'react'
import { Row, Col, Card } from 'antd'

export interface KpiCardDef {
  label: string
  value: React.ReactNode
  icon: React.ReactNode
  iconBg: string
  borderColor: string
}

interface KpiCardRowProps {
  cards: KpiCardDef[]
  /** Column span per breakpoint — defaults match the 4-card layout. */
  colProps?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number }
}

// Shared KPI stat-card strip — icon-circle badge + colored left border.
// Auto-scales value font size for long text (e.g. "1,50,69,025 kWh/yr")
// to prevent overflow on narrow columns.
const KpiCardRow: React.FC<KpiCardRowProps> = ({ cards, colProps = { xs: 24, sm: 12, lg: 6 } }) => (
  <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
    {cards.map((card) => {
      // Auto-scale font for long string values
      const valueStr = typeof card.value === 'string' ? card.value : ''
      const isLong = valueStr.length > 12
      const isVeryLong = valueStr.length > 18

      return (
        <Col
          key={card.label}
          xs={colProps.xs ?? 24}
          sm={colProps.sm ?? 12}
          md={colProps.md}
          lg={colProps.lg ?? 6}
          xl={colProps.xl}
        >
          <Card
            style={{
              borderLeft: `3.5px solid ${card.borderColor}`,
              borderRadius: 'var(--radius-lg, 12px)',
              boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(11,20,38,0.06))',
              background: 'var(--surface-1, #fff)',
              height: '100%',
            }}
            styles={{ body: { padding: '14px 16px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Icon circle */}
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: card.iconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {card.icon}
              </div>

              {/* Text block — minWidth:0 allows the flex child to shrink below
                  the natural content width so long values wrap instead of overflow. */}
              <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted, #8A93A6)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    lineHeight: 1.4,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {card.label}
                </div>
                <div
                  style={{
                    fontSize: isVeryLong ? 15 : isLong ? 19 : 26,
                    fontWeight: 700,
                    color: 'var(--text-primary, #0B1426)',
                    lineHeight: 1.25,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                    marginTop: 2,
                  }}
                >
                  {card.value}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      )
    })}
  </Row>
)

export default KpiCardRow
